import React, { useState } from 'react';
import { Box, CircularProgress, Container, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getAdminAiSettings, useUpdateAdminAiSettings } from '../../queries/adminAiSettings';
import { blurredActionSx } from '../CompanyProfile/blurStyles';
import AiInstructionSection from './AiInstructionSection';
import AiInstructionEditModal from './AiInstructionEditModal';

const TABS = [
  {
    id: 'source-processing',
    label: 'Source processing',
    enabled: true,
    field: 'sourceProcessingInstructions',
    title: 'Source processing instructions',
    description:
      'These instructions determine how TechXpander processes client documents and URLs into searchable knowledge. This knowledge is used by Company Profile, Understand and Engage.',
    saveMessage: 'Source processing instructions saved',
  },
  {
    id: 'public-web-research',
    label: 'Public Web Research',
    enabled: true,
    field: 'publicWebResearchInstructions',
    title: 'Public Web Research Instructions',
    description:
      'These instructions determine how TechXpander researches and selects relevant public information about clients and their products.',
    saveMessage: 'Public Web Research instructions saved',
  },
  {
    id: 'knowledge-update',
    label: 'Knowledge Update',
    enabled: true,
    field: 'knowledgeUpdateInstructions',
    title: 'Knowledge Update Instructions',
    description:
      'These instructions determine how TechXpander compares newly processed information with existing client knowledge and decides whether it is new, supporting, complementary, updated or conflicting.',
    saveMessage: 'Knowledge Update instructions saved',
  },
  {
    id: 'engage',
    label: 'Engage',
    enabled: true,
    field: 'engageMessageInstructions',
    title: 'Engage AI Instructions',
    description:
      'These are the complete instructions TechXpander uses to write and refine outbound messages in Engage. They are sent to the AI unchanged, together with the Company and Product Profiles, Prospect details, Prospect Research and conversation context.',
    saveMessage: 'Engage AI Instructions saved',
  },
  {
    id: 'prospect-research',
    label: 'Prospect research',
    enabled: true,
    field: 'prospectResearchInstructions',
    title: 'Prospect Research Instructions',
    description:
      'These instructions determine how TechXpander researches a Prospect company for a specific Client and Product when a Lead Agent clicks Generate in Engage. The research is stored on that Prospect. Core source and safety rules are controlled by TechXpander and cannot be overridden here.',
    saveMessage: 'Prospect research instructions saved',
  },
  { id: 'understand', label: 'Prospect research (Understand)', enabled: false },
  { id: 'general', label: 'General', enabled: false },
];

const AdminAiSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('source-processing');
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const { data: settings, isLoading } = useQuery(getAdminAiSettings());
  const { mutateAsync: saveSettings, isPending: isSaving } = useUpdateAdminAiSettings();

  const currentTab = TABS.find((tab) => tab.id === activeTab) || TABS[0];

  const openEditor = () => {
    setDraft(settings?.[currentTab.field] || '');
    setEditorOpen(true);
  };

  const handleSave = async () => {
    try {
      await saveSettings({ [currentTab.field]: draft });
      toast.success(currentTab.saveMessage);
      setEditorOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save instructions');
    }
  };

  return (
    <Container maxWidth={false} sx={{ my: 4, px: { xs: 2, md: 3 }, width: '100%', maxWidth: '100%' }}>
      <Typography variant='h5' fontWeight={700} mb={0.75}>
        AI Settings
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={3} sx={{ maxWidth: 720 }}>
        Configure the AI instructions and behavior that TechXpander uses across the platform.
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_event, next) => {
          setActiveTab(next);
          setEditorOpen(false);
        }}
        sx={{
          mb: 3,
          minHeight: 36,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontWeight: 600 },
        }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            disabled={!tab.enabled}
            sx={!tab.enabled ? { ...blurredActionSx, filter: 'blur(1.4px)', opacity: 0.55 } : undefined}
          />
        ))}
      </Tabs>

      {isLoading ? (
        <Box display='flex' justifyContent='center' py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <AiInstructionSection
          title={currentTab.title}
          description={currentTab.description}
          instructions={settings?.[currentTab.field] || ''}
          onEdit={openEditor}
        />
      )}

      <AiInstructionEditModal
        open={editorOpen}
        description={currentTab.description}
        value={draft}
        onChange={setDraft}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </Container>
  );
};

export default AdminAiSettingsPage;
